'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { type CSSProperties, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import ColorScheme from '@/components/colorScheme'
import Plus from '@/components/Plus'
import WaitlistForm from '@/components/WaitlistForm'
import useHasMounted from '@/hooks/useHasMounted'

type Props = {
  label?: string
  className?: string
  style?: CSSProperties
  modalColorScheme?: 'pea' | 'royal'
}

export default function NavWaitlistButton({
  label = 'Join Waitlist',
  className = '',
  style,
  modalColorScheme = 'royal',
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const hasMounted = useHasMounted()

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={className}
        style={style}
      >
        {label}
      </button>

      {hasMounted &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className="absolute inset-0 bg-soft-black/60"
                  aria-hidden="true"
                  onClick={() => setIsOpen(false)}
                />
                <ColorScheme colorScheme={modalColorScheme}>
                  <motion.div
                    role="dialog"
                    aria-modal="true"
                    className="relative w-full max-w-112.5 rounded text-(--color-foreground) bg-(--color-background)"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      aria-label="Close"
                      className="absolute right-3 top-3 md:-right-10 md:top-0 z-10 w-5 h-5 rotate-45 cursor-pointer text-(--color-foreground) md:text-greyscale-white hover:opacity-70 transition-opacity"
                    >
                      <Plus />
                    </button>
                    <WaitlistForm />
                  </motion.div>
                </ColorScheme>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  )
}
